/**
 * The chrome every campaign screen shares.
 *
 * Self-contained, like the feed builder's. A public route must keep rendering
 * whatever changes in the authenticated app's shell, and `src/App.js` already
 * suppresses the step indicator, onboarding and footer on these paths.
 *
 * §4 — FAQ and Pricing stay discoverable in the header throughout the journey,
 * and "Back to my opportunities" replaces nothing: it is added once the person
 * has a list, so navigating away is never a one-way door.
 */

import React, { useEffect } from 'react';
import {
  CampaignCta, CampaignFooter, CampaignFooterMinimal, CampaignResults,
} from './CampaignSiteSections';
import CampaignConsent from './CampaignConsent';
import { ensureCampaignTag } from '../../analytics/campaignTag';
import './Campaign.css';

export default function CampaignShell({
  audience, onBackToList, variant = 'wide',
  // The site's results section — marketing pages only. It is a pitch, and a
  // visitor already inside their own workspace is past being pitched to.
  showResults = false,
  // The demo form is the only page that suppresses the band: there, the
  // call to action would point at the page the visitor is already on.
  hideCta = false,
  onRequestDemo,
  // The plain variant (default since 5 Oct) strips the marketing furniture
  // from EVERY screen, not just the landing page — a clean first screen that
  // hands off to a navy "Request a demo" band the moment a list appears
  // reads as two different sites.
  plain = false,
  children,
}) {
  // Booted here rather than in `src/index.js` so the container loads on
  // campaign routes ONLY. The authenticated product is not part of this
  // measurement and has its own privacy posture; loading a marketing tag
  // across it would be a decision nobody has taken.
  useEffect(() => { ensureCampaignTag(); }, []);

  return (
    <div className="cmp-page">
      <header className="cmp-top-bar">
        <div className="cmp-top-bar-inner">
          {/* §4 brand asset — the real orange wordmark, never styled text with
              an orange dot. Hidden rather than showing a broken image if the
              asset is missing. */}
          <img
            src="/textOnlyLogoOrr-01.png"
            alt="Alchemiq"
            className="cmp-logo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {/* Q10, approved 30 Sep — the wordmark with "The relevance engine"
              beside it. */}
          <span className="cmp-tagline">The relevance engine</span>
          {/* §4 — FAQ and Pricing discoverable throughout. They point at real
              pages now; until those existed these links rendered blank. */}
          <nav className="cmp-nav">
            {onBackToList && (
              <button type="button" onClick={onBackToList}>Back to my opportunities</button>
            )}
            {audience && (
              <>
                <a href={`/c/${audience}/faq`}>FAQ</a>
                <a href={`/c/${audience}/pricing`}>Pricing</a>
              </>
            )}
          </nav>
        </div>
      </header>
      <main className={`cmp-main${variant === 'prose' ? ' cmp-main--prose' : ''}`}>{children}</main>
      {showResults && !plain && <CampaignResults />}
      {/* The closing band is on EVERY page. It was held back mid-funnel on
          the argument that someone waiting on a list is already converted;
          the call was made to show it throughout, so a visitor always has a
          way to ask for a demo from wherever they happen to be. */}
      {!hideCta && !plain && <CampaignCta audience={audience} onRequestDemo={onRequestDemo} />}
      {/* The cookie notice stays in both variants: it is a legal control,
          not marketing furniture. */}
      {plain ? <CampaignFooterMinimal /> : <CampaignFooter audience={audience} />}
      <CampaignConsent />
    </div>
  );
}
